import React, { useCallback, useId, useState } from 'react'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'
import { serverMessage } from '../../../../../shared/error/server-message'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { revokeKey, rotateKey } from '../api'
import { DEFAULT_GRACE, GRACE_CHOICES, graceText, reasonProblem } from '../key-words'
import { ApiKeySecretDialog } from './api-key-secret-dialog'

// A key's writes that ask first, shared by the list and the key's page:
// rotate (a grace for the old secret: 0, 1, 24 or 168 hours; ruling 17),
// revoke (an optional reason), and the one-time secret dialog of a create
// or a rotate. onChanged reads the page again after a write (the key or
// keys, and the organization's API access, for the page and the sidebar).
//
// The secret is held here only from the answer until "I've stored it":
// then this state lets go of it.
export const useKeyActions = ({ onChanged }) => {
  const ids = { grace: useId(), reason: useId() }
  const [rotating, setRotating] = useState(null)
  const [grace, setGrace] = useState(DEFAULT_GRACE)
  const [revoking, setRevoking] = useState(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const [revealed, setRevealed] = useState(null)

  const askRotate = useCallback(key => {
    setError(null)
    setGrace(DEFAULT_GRACE)
    setRotating({ id: key.id, name: key.name })
  }, [setError])

  const askRevoke = useCallback(key => {
    setError(null)
    setReason('')
    setRevoking({ id: key.id, name: key.name })
  }, [setError])

  // A just-made key's secret: { name, secret }.
  const reveal = useCallback(made => setRevealed({ name: made.name, secret: made.secret, rotated: false }), [])

  const rotate = async () => {
    if (busy || !rotating) return
    setBusy(true)
    setError(null)
    try {
      const answer = await rotateKey(rotating.id, grace)
      setBusy(false)
      setRevealed({ name: rotating.name, secret: answer && answer.secret, rotated: true, graceHours: grace })
      setRotating(null)
      if (onChanged) onChanged()
    } catch (err) {
      setBusy(false)
      setError(serverMessage(err, "The key couldn't be rotated."), err)
    }
  }

  const revoke = async () => {
    if (busy || !revoking) return
    const problem = reasonProblem(reason)
    if (problem) return setError(problem)
    setBusy(true)
    setError(null)
    try {
      await revokeKey(revoking.id, reason)
      setBusy(false)
      setRevoking(null)
      if (onChanged) onChanged()
    } catch (err) {
      setBusy(false)
      setError(serverMessage(err, "The key couldn't be revoked."), err)
    }
  }

  const dialogs = (
    <>
      <ConfirmDialog
        open={Boolean(rotating)}
        title={rotating ? `Rotate "${rotating.name}"?` : ''}
        body="A new secret is made and shown once. Choose how long the old secret keeps working, so whatever uses it can switch over first."
        confirmText="Rotate key"
        confirmClassName="btn sw-btn-primary"
        busy={busy}
        error={error}
        failure={failure}
        onConfirm={rotate}
        onCancel={() => setRotating(null)}>
        <div className="sw-label" id={ids.grace}>The old secret keeps working</div>
        <div className="ak-choices mb-3" role="radiogroup" aria-labelledby={ids.grace}>
          {GRACE_CHOICES.map(choice =>
            <label key={choice.hours}>
              <input type="radio" name={ids.grace} checked={grace === choice.hours} onChange={() => setGrace(choice.hours)} />
              {choice.label}
            </label>)}
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={Boolean(revoking)}
        title={revoking ? `Revoke "${revoking.name}"?` : ''}
        body="Requests made with this key stop working on the next request. This can't be undone: make a new key to replace it."
        confirmText="Revoke key"
        busy={busy}
        error={error}
        failure={failure}
        onConfirm={revoke}
        onCancel={() => setRevoking(null)}>
        <div className="mb-3">
          <label className="sw-label" htmlFor={ids.reason}>Reason (optional)</label>
          <textarea id={ids.reason} className="form-control" rows={2} maxLength={600} value={reason} onChange={e => setReason(e.target.value)} />
        </div>
      </ConfirmDialog>

      {revealed &&
        <ApiKeySecretDialog
          keyName={revealed.name}
          secret={revealed.secret}
          rotated={revealed.rotated}
          graceText={revealed.rotated ? graceText(revealed.graceHours) : null}
          onDone={() => setRevealed(null)} />}
    </>
  )

  return { askRotate, askRevoke, reveal, dialogs }
}
